import React, { useState, useEffect } from 'react'
import { Event } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { toDateInputValue } from '../utils/dates'

type DuplicateEventModalMetaProps = {
    title: string
    nameLabel: string
    namePlaceholder: string
    startDateLabel: string
    endDateLabel: string
    startTimeLabel: string
    endTimeLabel: string
    submitButton: string
    submittingButton: string
    cancelButton: string
    errorMessage: string
    description: string
}

type Props = {
    meta: DuplicateEventModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (eventData: any) => Promise<void>
    originalEvent: Event | null
}

export const DuplicateEventModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, originalEvent } = props

    const [formData, setFormData] = useState({
        name: '',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: ''
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Populate form when modal opens with original event data
    useEffect(() => {
        if (originalEvent && isOpen) {
            setFormData({
                name: originalEvent.name ? `${originalEvent.name} (Copy)` : '',
                start_date: toDateInputValue(originalEvent.start_date),
                end_date: toDateInputValue(originalEvent.end_date),
                start_time: originalEvent.start_time ?? '',
                end_time: originalEvent.end_time ?? ''
            })
        } else if (!isOpen) {
            setFormData({
                name: '',
                start_date: '',
                end_date: '',
                start_time: '',
                end_time: ''
            })
            setError(null)
        }
    }, [originalEvent, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            // Format time fields to ensure they're in HH:MM format (remove seconds if present)
            const formattedData = {
                ...formData,
                start_time: formData.start_time.substring(0, 5),
                end_time: formData.end_time.substring(0, 5)
            }

            await onSubmit(formattedData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="lg"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="duplicate-event-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="duplicate-event-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                        {meta.description}
                    </p>

                    <div className="space-y-4">
                        {/* Event Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.nameLabel}
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={meta.namePlaceholder}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                            />
                        </div>

                        {/* Date Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.startDateLabel}
                                </label>
                                <input
                                    type="date"
                                    id="start_date"
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.endDateLabel}
                                </label>
                                <input
                                    type="date"
                                    id="end_date"
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        {/* Time Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.startTimeLabel}
                                </label>
                                <input
                                    type="time"
                                    id="start_time"
                                    name="start_time"
                                    value={formData.start_time}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                                />
                            </div>

                            <div>
                                <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.endTimeLabel}
                                </label>
                                <input
                                    type="time"
                                    id="end_time"
                                    name="end_time"
                                    value={formData.end_time}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    )
}
